import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, ToggleButton, ToggleButtonGroup, TextField, MenuItem,
  CircularProgress, Chip, Paper,
} from '@mui/material';
import { EventAvailable } from '@mui/icons-material';
import axios from 'axios';
import { LineChart } from './LernAlytics';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const iso = d => d.toISOString().slice(0, 10);

// Overview attendance snapshot: a 14-day attendance-rate trend for the class or
// one of the teacher's subject classes, with a Class / Subject toggle — the
// dashboard's attendance chart. Reuses /attendance/report.
export default function AttendanceSnapshot({ selectedClass, className, subjectClasses = [], year, term, onOpenAttendance }) {
  const subjectOptions = useMemo(
    () => subjectClasses.filter(s => s.kind === 'core' && s.classCode && s.areaCode),
    [subjectClasses]
  );
  const [scope, setScope] = useState('class');
  const [subjectKey, setSubjectKey] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!subjectKey && subjectOptions.length) setSubjectKey(`${subjectOptions[0].classCode}|${subjectOptions[0].areaCode}`);
  }, [subjectOptions, subjectKey]);

  // If there's no class to show, default the toggle to Subject.
  useEffect(() => { if (!selectedClass && subjectOptions.length) setScope('subject'); }, [selectedClass, subjectOptions.length]);

  const load = useCallback(async () => {
    const params = { academicYear: year, term };
    if (scope === 'class') {
      if (!selectedClass) { setReport(null); return; }
      Object.assign(params, { scope: 'class', classCode: selectedClass, areaCode: '' });
    } else {
      if (!subjectKey) { setReport(null); return; }
      const [classCode, areaCode] = subjectKey.split('|');
      Object.assign(params, { scope: 'subject', classCode, areaCode });
    }
    const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 13);
    params.from = iso(from); params.to = iso(to);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/academics/attendance/report`, { headers: hdrs(), params });
      setReport(data);
    } catch (err) { setReport(null); } finally { setLoading(false); }
  }, [scope, subjectKey, selectedClass, year, term]);

  useEffect(() => { load(); }, [load]);

  const points = useMemo(
    () => (report?.byDate || []).map(d => ({ label: d.date.slice(5), value: d.rate })),
    [report]
  );
  const avgRate = useMemo(() => {
    const b = report?.byDate || [];
    return b.length ? Math.round(b.reduce((a, d) => a + d.rate, 0) / b.length) : null;
  }, [report]);
  const todayRow = useMemo(
    () => (report?.byDate || []).find(d => d.date === iso(new Date())),
    [report]
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          <EventAvailable sx={{ verticalAlign: 'middle', mr: 0.5 }} fontSize="small" />
          Attendance — last 14 days
        </Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
            <ToggleButton value="class" disabled={!selectedClass} sx={{ py: 0.2, px: 1.2 }}>Class</ToggleButton>
            <ToggleButton value="subject" disabled={!subjectOptions.length} sx={{ py: 0.2, px: 1.2 }}>Subject</ToggleButton>
          </ToggleButtonGroup>
          {scope === 'subject' && (
            <TextField select size="small" value={subjectKey} onChange={e => setSubjectKey(e.target.value)} sx={{ minWidth: 200 }}>
              {subjectOptions.map(o => (
                <MenuItem key={`${o.classCode}|${o.areaCode}`} value={`${o.classCode}|${o.areaCode}`}>{o.label}</MenuItem>
              ))}
            </TextField>
          )}
          {onOpenAttendance && (
            <Button size="small" variant="outlined" startIcon={<EventAvailable />} onClick={onOpenAttendance}>
              Take / edit
            </Button>
          )}
        </Box>
      </Box>

      {loading ? <CircularProgress size={24} /> : !report || report.totalSheets === 0 ? (
        <Typography variant="body2" color="text.secondary">No attendance marked in the last 14 days.</Typography>
      ) : (
        <Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
            <Chip size="small" label={`${report.className}${report.scope === 'subject' ? ' · subject' : ''}`} />
            {avgRate != null && <Chip size="small" color={avgRate >= 90 ? 'success' : avgRate >= 75 ? 'warning' : 'error'} label={`Avg ${avgRate}%`} />}
            <Typography variant="caption" color="text.secondary">
              {todayRow
                ? `Today: ${todayRow.present} present, ${todayRow.absent} absent, ${todayRow.late} late (${todayRow.rate}%)`
                : 'Today: not yet taken'}
            </Typography>
          </Box>
          <LineChart points={points} height={180} color="#1565c0" />
        </Box>
      )}
    </Paper>
  );
}
